/**
 * Wie ein Modell heißt — abgeleitet aus seiner Kennung, nicht aufgeschrieben.
 *
 * ⚠️⚠️ 14.09.2026 — DIE ANZEIGE LOG ÜBER DAS MODELL.
 *
 * Martin, mit Bild der Eingabezeile: „da steht immer noch Gemini 3.1". Und
 * beim Gemini-Backup stand „Gemini 3.7 Flash", während in `server/models.ts`
 * längst `gemini-3.8-flash` als Kennung lief (seit pre.2869).
 *
 * Der Grund war eine Asymmetrie: Grok leitete seinen Namen SCHON aus der
 * Kennung ab (`grok-4.6` → „Grok 4.6", Stand 20.08., weil „Grok 4" für beide
 * Stufen gleich war und die Zeile „Jetzt" heißt). Gemini nicht — sein Name
 * stand an DREI Stellen fest verdrahtet:
 *   frontend/lib/settings/modell-status.ts
 *   frontend/lib/settings.ts
 *   server/runtime-modell.ts
 * Wer die Kennung im Register änderte, änderte den Namen nicht mit. Die
 * Anzeige behauptete dann etwas, das nicht stimmte — und das ist schlimmer
 * als gar keine Anzeige, weil man sich darauf verlässt.
 *
 * Das Modell-Register (server/models.ts) ist laut Projekt „die EINE
 * Wahrheit". Dann muss der Name ihr folgen, nicht danebenstehen.
 */

/**
 * Menschlicher Name zu einer Modell-Kennung. Unbekanntes gibt die Kennung
 * selbst zurück — lieber roh und wahr als schön und falsch.
 */
export function nameFuerModell(id: string): string {
  const k = (id ?? "").trim();
  if (!k) return "unbekannt";

  // Grok: die Zahl steckt in der Kennung (grok-4.6 → Grok 4.6).
  if (/^grok/i.test(k)) return `Grok ${k.replace(/^grok-/i, "") || "4"}`;

  // Gemini: Version und Stufe ebenfalls aus der Kennung lesen, damit ein
  // Wechsel im Register (3.7 → 3.8) hier ankommt, ohne dass jemand daran
  // denken muss.
  const g = /^gemini-(\d+(?:\.\d+)?)-?(flash|pro)?/i.exec(k);
  if (g) {
    const version = g[1] ?? "";
    // 23.09.2026: Live-Modelle eigens. „gemini-3.8-live" trägt keine Stufe —
    // bisher wurde daraus „Gemini 3.8 Flash", ein Name, den es nicht gibt.
    const live = /-live\b/i.test(k);
    const stufe = /pro/i.test(k)
      ? "Pro"
      : /flash-lite/i.test(k)
        ? "Flash Lite"
        : /flash/i.test(k) || !live
          ? "Flash"
          : "";
    return `Gemini ${version} ${stufe}${live ? " Live" : ""}`.replace(/\s+/g, " ").trim();
  }

  // Claude läuft über das Abo; die Stufe ist das, was Martin unterscheidet.
  // ⚠️ 23.09.2026 — jetzt MIT Version, siehe claudeName().
  const claude = claudeName(k);
  if (claude) return `${claude} · Abo`;

  return k;
}

/**
 * Die Version aus einer Claude-Kennung — oder "", wenn keine drinsteht.
 *
 *     claude-opus-5-5            → "5.5"
 *     claude-opus-5              → "5"
 *     claude-fable-5-1           → "5.1"
 *     claude-haiku-4-5-20251001  → "4.5"   (Datumsanhang fällt weg)
 *     claude-cli-warm-opus       → ""      (ein Kürzel, keine Version)
 *
 * ⚠️⚠️ 23.09.2026 — BIS HEUTE STAND HIER „Opus · Abo", OHNE ZAHL.
 *
 * Martin: *„Bitte alle Opus-5-Versionen auf das höchste Level anpassen und
 * entsprechend im Setting auch sichtbar machen."* Sichtbar war nichts: jede
 * Opus-Kennung hieß „Opus · Abo", ob 4.8, 5 oder 5.5. Am selben Morgen hatte
 * ein CLI-Update das Kürzel `opus` still auf 5.5 umgelegt — und keine
 * Anzeige der ganzen App hätte es verraten.
 *
 * ⚠️ Abgeleitet, nicht aufgeschrieben: Wer im Register die Kennung ändert,
 *    ändert damit auch, was hier steht. Eine zweite Liste mit Namen wäre
 *    beim nächsten Modell wieder falsch — genau das Gemini-Problem von oben.
 */
function claudeVersion(id: string): string {
  const m = /^claude-[a-z]+-(\d+(?:-\d+)?)(?:-\d{8})?$/i.exec((id ?? "").trim());
  if (!m) return "";
  return (m[1] ?? "").replace("-", ".");
}

/** „Opus 5.5", „Fable 5.1", „Haiku 4.5" — oder "", wenn es kein Claude ist. */
export function claudeName(id: string): string {
  const k = (id ?? "").trim();
  const familie = /opus/i.test(k)
    ? "Opus"
    : /fable/i.test(k)
      ? "Fable"
      : /haiku/i.test(k)
        ? "Haiku"
        : /sonnet/i.test(k)
          ? "Sonnet"
          : /claude/i.test(k)
            ? "Sonnet"
            : "";
  if (!familie) return "";
  const version = claudeVersion(k);
  return version ? `${familie} ${version}` : familie;
}
